/**
 * Presenter-side control for demo voting. Lives as a quiet utility in the app
 * header: one wide dialog with the QR on the left (constant) and, on the
 * right, a reveal gate — a live vote counter until the presenter hits "Show
 * results", then the full results panel (distribution + KPIs + themed
 * clusters).
 *
 * Polling is scoped to what's on screen: the summary polls whenever the dialog
 * is open (the hidden counter and the revealed distribution both stay live);
 * themes poll only once results are revealed.
 */

import { Vote } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useVoteSummary, useVoteThemes } from "@/lib/api";
import { cn } from "@/lib/utils";

import { VoteResults } from "./vote-results";

const POLL_MS = 5000;

/**
 * @param triggerClassName Overrides the default quiet-utility trigger styling so
 *   the shell can restyle the trigger (e.g. as a full-width footer row). The
 *   dialog itself is unchanged.
 * @param iconOnly Drops the "Audience voting" label (keeping it as the button's
 *   accessible name) so tight chrome can show just the icon.
 */
export function PresenterVotingDialog({
  triggerClassName,
  iconOnly = false,
}: { triggerClassName?: string; iconOnly?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  const [revealed, setRevealed] = useState(false);

  // Summary polls while open (both states need the live count); themes only
  // once revealed. `enabled` gates the poll, so a plain interval is enough.
  const summaryQuery = useVoteSummary({ enabled: open, refetchInterval: POLL_MS });
  const themesQuery = useVoteThemes({ enabled: open && revealed, refetchInterval: POLL_MS });

  const summary = summaryQuery.data;
  const themes = themesQuery.data;
  const total = summary?.total ?? 0;

  const voteUrl = `${window.location.origin}/vote`;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Re-arm the reveal gate on every close: revealing is a deliberate
        // presenter action, so each open should start hidden.
        if (!next) {
          setRevealed(false);
        }
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={iconOnly ? "Audience voting" : undefined}
          title={iconOnly ? "Audience voting" : undefined}
          className={cn(
            triggerClassName ??
              "text-muted hover:text-foreground inline-flex items-center gap-2 text-xs font-medium transition-colors",
          )}
        >
          <Vote className="h-4 w-4" aria-hidden />
          {!iconOnly && "Audience voting"}
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-heading">Audience voting</DialogTitle>
          <DialogDescription>
            Attendees scan to rate this demo. Reveal the results when you&apos;re ready to share
            them with the room.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-6 md:flex-row">
          {/* Left: the QR, constant across both states. */}
          <div className="flex flex-col items-center gap-3 md:w-[280px] md:shrink-0">
            <div className="border-border rounded-lg border bg-white p-4">
              <QRCodeSVG value={voteUrl} size={224} bgColor="#ffffff" fgColor="#000000" level="M" />
            </div>
            <span className="text-muted text-sm break-all select-all">{voteUrl}</span>
          </div>

          {/* Right: reveal gate → results. */}
          <div className="min-w-0 flex-1">
            {revealed ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-foreground text-base font-bold">Live results</h3>
                  <button
                    type="button"
                    onClick={() => setRevealed(false)}
                    className="text-muted hover:text-foreground text-xs font-medium transition-colors"
                  >
                    Hide results
                  </button>
                </div>
                <VoteResults
                  summary={summary}
                  themes={themes}
                  themesLoading={themesQuery.isLoading}
                />
              </div>
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-5 py-8 text-center">
                <div className="space-y-1">
                  <h3 className="font-heading text-foreground text-xl font-bold">
                    Results are hidden
                  </h3>
                  <p className="text-muted text-sm">
                    Reveal when you&apos;re ready to share the room&apos;s feedback.
                  </p>
                </div>
                <p className="text-foreground text-sm font-medium">
                  {summary ? `${total} votes so far` : "Waiting for votes…"}
                </p>
                <button
                  type="button"
                  onClick={() => setRevealed(true)}
                  className="bg-accent text-accent-foreground hover:bg-accent/90 inline-flex h-10 items-center justify-center rounded-md px-4 text-sm font-medium transition-colors"
                >
                  Show results
                </button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
